import React, { useState, useEffect } from "react";
import { Collapse, Card, Button, Form } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronRight } from "@fortawesome/free-solid-svg-icons";
import Select from "react-select";
import ResultDisplay from "components/FlipPanel/components/ResultDisplay";
import AiOverviewTable from "components/MarkdownRender/tables/AiOverviewTable";

const ContentOutlineSection = ({ 
  isOpen, 
  onToggle, 
  initialKeyword = "",
  initialFormData = null,
  sourceType = "keyword",
  onSubmit,
  onDraftSubmit,
  onAiOverviewSubmit,
  isLoading = false,
  isDraftLoading = false,
  isAiOverviewLoading = false,
  isAnyLoading = false,
  onInsufficientCredits,
  outlineResult = null,
  draftResult = null,
  aiOverviewResult = null,
  outlineInputs = null,
  onCopy = null
}) => {
  const [title, setTitle] = useState(initialFormData?.title || "");
  const [keyword, setKeyword] = useState(initialFormData?.keyword || initialKeyword);
  const [brand, setBrand] = useState(initialFormData?.brand || "");
  const [model, setModel] = useState(initialFormData?.model ? { value: initialFormData.model, label: `Writer ${initialFormData.model.replace('writer_', '')}` } : { value: "writer_1", label: "Writer 1" });
  const [wordCount, setWordCount] = useState(initialFormData?.wordCount || 800);
  const [wordCountError, setWordCountError] = useState("");
  const [noQueries, setNoQueries] = useState(5);
  
  // Notification states
  const [showDraftNotification, setShowDraftNotification] = useState(false);
  const [showErrorNotification, setShowErrorNotification] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const modelOptions = [
    { value: "writer_1", label: "Writer 1" },
    { value: "writer_2", label: "Writer 2" },
    { value: "writer_3", label: "Writer 3" },
    { value: "writer_4", label: "Writer 4" }
  ];

  useEffect(() => {
    setKeyword(initialKeyword);
  }, [initialKeyword]);

  useEffect(() => {
    if (initialFormData) {
      setTitle(initialFormData.title || "");
      setKeyword(initialFormData.keyword || initialKeyword);
      setBrand(initialFormData.brand || "");
      setModel(initialFormData.model ? { value: initialFormData.model, label: `Writer ${initialFormData.model.replace('writer_', '')}` } : { value: "writer_1", label: "Writer 1" });
      setWordCount(initialFormData.wordCount || 800);
    }
  }, [initialFormData, initialKeyword]);


  const handleSubmit = () => {
    if (!title.trim() || !keyword.trim() || !brand.trim() || isAnyLoading) return;
    
    // Call the parent's onSubmit to handle the API call and state management
    if (onSubmit) {
      onSubmit({
        title: title.trim(),
        keyword: keyword.trim(),
        brand: brand.trim(),
        model: model.value,
        wordCount,
        sourceType
      });
    }
  };

  const handleGenerateDraft = () => {
    if (!outlineResult || !keyword.trim() || isAnyLoading) return;
    
    // Call the parent's onDraftSubmit to handle the API call and state management
    if (onDraftSubmit) {
      onDraftSubmit({
        seo_outline: outlineResult,
        primary_keyword: keyword.trim()
      });
    }
  };

  const handleGenerateAiOverview = () => {
    if (!draftResult || isAnyLoading) return;
    
    // Call the parent's onAiOverviewSubmit to handle the API call and state management
    if (onAiOverviewSubmit) {
      onAiOverviewSubmit({
        text: draftResult,
        no_queries: noQueries
      });
    }
  };

  const handleReset = () => {
    setTitle("");
    setBrand("");
    setModel({ value: "writer_1", label: "Writer 1" });
    setWordCount(800);
    setWordCountError("");
    setNoQueries(5);
    
    // Reset notification states
    setShowDraftNotification(false);
    setShowErrorNotification(false);
    setErrorMessage("");
  };


  return (
    <div className="mb-3">
      <Card className="border-0 shadow-sm">
        <Card.Header className="bg-white border-0 p-0">
          <Button
            className="w-100 text-start border-0 bg-transparent text-dark d-flex align-items-center justify-content-between p-3"
            onClick={onToggle}
            aria-controls="collapse-outline"
            aria-expanded={isOpen}
            style={{ cursor: 'pointer' }}
          >
            <div className="d-flex align-items-center">
              <FontAwesomeIcon
                icon={isOpen ? faChevronDown : faChevronRight}
                className="me-3 text-primary"
              />
              <div>
                <div className="fw-bold">Content Outline</div>
                <small className="text-muted">
                  {sourceType === "url" ? "Page-level content structure" : "Keyword-focused content structure"}
                </small>
              </div>
            </div>
          </Button>
        </Card.Header>
        <Collapse in={isOpen}>
          <div id="collapse-outline">
            <Card.Body className="pt-0">
              <p className="text-muted small mb-3">
                {sourceType === "url" 
                  ? "Create a comprehensive outline based on the page content and structure."
                  : "Build a detailed content outline optimized for your target keyword."
                }
              </p>
              
              <Form>
                <Form.Group className="mb-3">
                  <Form.Label className="small fw-bold">Title</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter content title"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    size="sm"
                  />
                </Form.Group>
                
                <Form.Group className="mb-3">
                  <Form.Label className="small fw-bold">Primary Keyword</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter primary keyword"
                    value={keyword}
                    onChange={(e) => setKeyword(e.target.value)}
                    size="sm"
                  />
                </Form.Group>
                
                <Form.Group className="mb-3">
                  <Form.Label className="small fw-bold">Brand</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter brand name"
                    value={brand}
                    onChange={(e) => setBrand(e.target.value)}
                    size="sm"
                  />
                </Form.Group>
                
                <div className="row mb-3">
                  <div className="col-6">
                    <Form.Label className="small fw-bold">Writer Model</Form.Label>
                    <Select
                      value={model}
                      onChange={setModel}
                      options={modelOptions}
                      styles={{
                        control: (base) => ({
                          ...base,
                          minHeight: "31px",
                          fontSize: "14px"
                        })
                      }}
                    />
                  </div>
                  <div className="col-6">
                    <Form.Label className="small fw-bold">Word Count</Form.Label>
                    <Form.Control
                      type="number"
                      placeholder="800"
                      value={wordCount}
                      onChange={(e) => {
                        const value = parseInt(e.target.value) || 800;
                        // Check if user tried to enter more than 1200
                        if (value > 1200) {
                          setWordCountError("Word count cannot exceed 1,200 words");
                          setWordCount(1200);
                        } else {
                          setWordCountError("");
                          setWordCount(value);
                        }
                      }}
                      onBlur={(e) => {
                        const value = parseInt(e.target.value) || 800;
                        if (value > 1200) {
                          setWordCountError("Word count cannot exceed 1,200 words");
                        } else {
                          setWordCountError("");
                        }
                      }}
                      isInvalid={!!wordCountError}
                      size="sm"
                      min="100"
                      max="1200"
                    />
                    {wordCountError && (
                      <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>
                        {wordCountError}
                      </Form.Control.Feedback>
                    )}
                    <small className="text-warning mt-1 d-block">
                      <i className="fas fa-exclamation-triangle me-1"></i>
                      Word count cannot exceed over 1,200 words
                    </small>
                  </div>
                </div>
                
                <div className="row g-2">
                  <div className="col-8">
                    <div className="d-grid">
                      <Button 
                        variant="success" 
                        size="sm"
                        onClick={handleSubmit}
                        disabled={!title.trim() || !keyword.trim() || !brand.trim() || isLoading || isAnyLoading}
                        style={{
                          cursor: (!title.trim() || !keyword.trim() || !brand.trim() || isLoading || isAnyLoading) ? 'not-allowed' : 'pointer',
                          opacity: (!title.trim() || !keyword.trim() || !brand.trim() || isLoading || isAnyLoading) ? 0.7 : 1,
                          backgroundColor: (!title.trim() || !keyword.trim() || !brand.trim() || isLoading || isAnyLoading) ? '#6c757d' : '',
                          borderColor: (!title.trim() || !keyword.trim() || !brand.trim() || isLoading || isAnyLoading) ? '#6c757d' : '',
                          color: (!title.trim() || !keyword.trim() || !brand.trim() || isLoading || isAnyLoading) ? '#ffffff' : ''
                        }}
                      >
                        {isLoading ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Creating...
                          </>
                        ) : outlineResult ? (
                          "Outline Created"
                        ) : (
                          "Create Outline"
                        )}
                      </Button>
                    </div>
                  </div>
                  <div className="col-4">
                    <div className="d-grid">
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={handleReset}
                        disabled={isAnyLoading || (!title.trim() && !brand.trim() && model.value === "writer_1" && wordCount === 800 && !outlineResult && !draftResult)}
                        style={{
                          cursor: (isAnyLoading || (!title.trim() && !brand.trim() && model.value === "writer_1" && wordCount === 800 && !outlineResult && !draftResult)) ? 'not-allowed' : 'pointer',
                          opacity: (isAnyLoading || (!title.trim() && !brand.trim() && model.value === "writer_1" && wordCount === 800 && !outlineResult && !draftResult)) ? 0.7 : 1,
                          backgroundColor: (isAnyLoading || (!title.trim() && !brand.trim() && model.value === "writer_1" && wordCount === 800 && !outlineResult && !draftResult)) ? '#f8f9fa' : '',
                          borderColor: (isAnyLoading || (!title.trim() && !brand.trim() && model.value === "writer_1" && wordCount === 800 && !outlineResult && !draftResult)) ? '#6c757d' : '',
                          color: (isAnyLoading || (!title.trim() && !brand.trim() && model.value === "writer_1" && wordCount === 800 && !outlineResult && !draftResult)) ? '#6c757d' : ''
                        }}
                      >
                        Reset
                      </Button>
                    </div>
                  </div>
                </div>
              </Form>

              {/* Content Outline Display */}
              {outlineResult && keyword && (
                <div className="mt-4">
                  <p className="d-flex fw-bold mb-3">
                    Content outline for
                    <span className="text-primary">&nbsp;"{keyword}"</span>
                  </p>
                </div>
              )}

              <ResultDisplay
                result={outlineResult}
                title="Generated Content Outline"
                type="markdown"
                inputParameters={outlineInputs}
                inputParametersLabel="Generated with"
                className="mb-4"
                onCopy={onCopy}
              />

              {outlineResult && (
                <div className="mt-3">
                  <Button
                    variant="success"
                    size="sm"
                    onClick={handleGenerateDraft}
                    disabled={!outlineResult || isAnyLoading}
                    className="me-2"
                    style={{
                      cursor: (!outlineResult || isAnyLoading) ? 'not-allowed' : 'pointer',
                      opacity: (!outlineResult || isAnyLoading) ? 0.7 : 1
                    }}
                  >
                    {isDraftLoading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Generating Draft...
                      </>
                    ) : draftResult ? (
                      "Draft Generated"
                    ) : (
                      "Generate Draft"
                    )}
                  </Button>
                  <small className="text-muted">
                    This will use the content outline and primary keyword to generate a full draft
                  </small>
                </div>
              )}

              {/* Generated Draft Display */}
              <ResultDisplay
                result={draftResult}
                title="Generated Draft"
                type="markdown"
                showInputParameters={false}
                className="mb-4"
                onCopy={onCopy}
              />

              {/* AI Overview Generation Section */}
              {draftResult && (
                <div className="mt-3">
                  <div className="row mb-3">
                    <div className="col-6">
                      <Form.Label className="small fw-bold">Number of Queries</Form.Label>
                      <Form.Control
                        type="number"
                        placeholder="5"
                        value={noQueries}
                        onChange={(e) => setNoQueries(parseInt(e.target.value) || 5)}
                        size="sm"
                        min="1"
                        max="10"
                      />
                    </div>
                  </div>
                  
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={handleGenerateAiOverview}
                    disabled={!draftResult || isAnyLoading}
                    className="me-2"
                    style={{
                      cursor: (!draftResult || isAnyLoading) ? 'not-allowed' : 'pointer',
                      opacity: (!draftResult || isAnyLoading) ? 0.7 : 1
                    }}
                  >
                    {isAiOverviewLoading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Generating AI Overview...
                      </>
                    ) : aiOverviewResult ? (
                      "AI Overview Generated"
                    ) : (
                      "Generate AI Overview"
                    )}
                  </Button>
                  <small className="text-muted">
                    Generate an AI overview from the content draft
                  </small>
                </div>
              )}

              {/* Generated AI Overview Display */}
              <AiOverviewTable
                result={aiOverviewResult}
                title="Generated AI Overview"
                onCopy={onCopy}
              />

            </Card.Body>
          </div>
        </Collapse>
      </Card>
    </div>
  );
};

export default ContentOutlineSection;
